<template>
  <main class="condolence-tool">
    <section class="condolence-editor">
      <div class="container-fluid tool-container">
        <div class="tool-heading">
          <span>Taziye aracı</span>
          <h1>Taziye Duyurusu Oluştur</h1>
          <p>Vefat eden hemşehrimiz için fotoğraflı veya sade, paylaşılabilir portre taziye duyurusu hazırlayın.</p>
        </div>

        <div class="tool-layout">
          <form class="tool-form" @submit.prevent="downloadAsPng">
            <div>
              <label for="deceasedName" class="form-label">Vefat Eden</label>
              <input id="deceasedName" v-model="deceasedName" type="text" class="form-control" placeholder="Ad Soyad" />
            </div>

            <div>
              <label for="gender" class="form-label">Cinsiyet</label>
              <select id="gender" v-model="gender" class="form-control">
                <option value="erkek">Erkek - Merhum</option>
                <option value="kadin">Kadın - Merhume</option>
              </select>
            </div>

            <div>
              <label for="message" class="form-label">Taziye Mesajı</label>
              <textarea
                id="message"
                v-model="message"
                class="form-control"
                rows="5"
                placeholder="Taziye mesajını yazın."
              />
            </div>

            <div>
              <label for="photo" class="form-label">Fotoğraf</label>
              <input id="photo" type="file" class="form-control" accept="image/*" @change="handlePhotoUpload" />
              <button v-if="photoPreview" type="button" class="btn btn-sm btn-outline-primary mt-2" @click="clearPhoto">
                Fotoğrafı Kaldır
              </button>
            </div>

            <div class="form-grid">
              <div>
                <label for="place" class="form-label">Taziye Yeri</label>
                <input id="place" v-model="place" type="text" class="form-control" placeholder="Opsiyonel" />
              </div>
              <div>
                <label for="date" class="form-label">Tarih / Saat</label>
                <input id="date" v-model="date" type="text" class="form-control" placeholder="Opsiyonel" />
              </div>
            </div>

            <div>
              <label for="contact" class="form-label">İletişim</label>
              <input id="contact" v-model="contact" type="text" class="form-control" placeholder="Opsiyonel" />
            </div>

            <div class="tool-actions">
              <button type="submit" class="btn btn-primary">
                <i class="fa fa-download me-2"></i>PNG Olarak İndir
              </button>
            </div>
          </form>

          <div class="preview-shell">
            <div class="preview-note">
              <i class="fa fa-mobile-screen-button"></i>
              1080 x 1350 portre paylaşım formatı
            </div>

            <div ref="posterFrame" class="poster-scale">
              <div ref="captureArea" class="condolence-poster" :style="{ '--poster-scale': posterScale }">
                <div class="poster-glow poster-glow-one"></div>
                <div class="poster-glow poster-glow-two"></div>
                <div class="poster-line poster-line-top"></div>
                <div class="poster-line poster-line-bottom"></div>

                <section class="poster-header">
                  <h2>Vefat Duyurusu</h2>
                </section>

                <section class="person-area">
                  <div class="photo-frame" :class="{ empty: !photoPreview }">
                    <img v-if="photoPreview" :src="photoPreview" alt="">
                    <i v-else class="fa fa-ribbon"></i>
                  </div>
                  <p>{{ honorific }}</p>
                  <h3>{{ normalizedName }}</h3>
                </section>

                <section class="message-card">
                  <p :class="['message-text', messageLengthClass]">{{ normalizedMessage }}</p>
                </section>

                <section v-if="infoCount" :class="['info-grid', `count-${infoCount}`]">
                  <div v-if="place.trim()">
                    <small>Taziye Yeri</small>
                    <span>
                      <i class="fa fa-location-dot"></i>
                      <strong>{{ place }}</strong>
                    </span>
                  </div>
                  <div v-if="date.trim()">
                    <small>Tarih / Saat</small>
                    <span>
                      <i class="fa fa-calendar-days"></i>
                      <strong>{{ date }}</strong>
                    </span>
                  </div>
                  <div v-if="contact.trim()">
                    <small>İletişim</small>
                    <span>
                      <i class="fa fa-phone-alt"></i>
                      <strong>{{ contact }}</strong>
                    </span>
                  </div>
                </section>

                <footer class="poster-footer">
                  <img src="/img/logo.jpg" alt="Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği Logosu">
                  <div>
                    <strong>Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği</strong>
                    <span>Kütük No: 06-158-130</span>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import html2canvas from 'html2canvas'

const captureArea = ref(null)
const posterFrame = ref(null)
const posterScale = ref('0.52')
const deceasedName = ref('')
const gender = ref('erkek')
const message = ref("Vefat haberini derin bir üzüntüyle öğrenmiş bulunuyoruz. Merhuma Allah'tan rahmet; ailesine, yakınlarına ve tüm sevenlerine başsağlığı dileriz.")
const place = ref('')
const date = ref('')
const contact = ref('')
const photoPreview = ref('')

const normalizedName = computed(() => deceasedName.value.trim() || 'Ad Soyad')
const honorific = computed(() => gender.value === 'kadin' ? 'Merhume' : 'Merhum')
const normalizedMessage = computed(() => message.value.trim() || "Merhuma Allah'tan rahmet; ailesine, yakınlarına ve sevenlerine başsağlığı dileriz.")
const infoCount = computed(() => [place.value, date.value, contact.value].filter((item) => item.trim()).length)
const messageLengthClass = computed(() => {
  const length = normalizedMessage.value.length
  if (length > 360) return 'very-long'
  if (length > 240) return 'long'
  if (length > 140) return 'medium'
  return 'short'
})

let resizeObserver

const updatePosterScale = () => {
  const width = posterFrame.value?.clientWidth || 560
  posterScale.value = String(Math.min(width / 1080, 1))
}

onMounted(() => {
  updatePosterScale()
  resizeObserver = new ResizeObserver(updatePosterScale)
  if (posterFrame.value) resizeObserver.observe(posterFrame.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

const handlePhotoUpload = (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = () => {
    photoPreview.value = String(reader.result || '')
  }
  reader.readAsDataURL(file)
}

const clearPhoto = () => {
  photoPreview.value = ''
}

const downloadAsPng = async () => {
  if (!captureArea.value) return

  const previousTransform = captureArea.value.style.transform
  captureArea.value.style.transform = 'none'

  let canvas
  try {
    canvas = await html2canvas(captureArea.value, {
      backgroundColor: '#0f172b',
      scale: 1,
      useCORS: true,
      width: 1080,
      height: 1350,
      windowWidth: 1080,
      windowHeight: 1350
    })
  } finally {
    captureArea.value.style.transform = previousTransform
  }

  const link = document.createElement('a')
  const today = new Date().toISOString().slice(0, 10)
  link.download = `ankara-agrililar-taziye-${today}.png`
  link.href = canvas.toDataURL('image/png')
  link.click()
}
</script>

<style scoped>
.condolence-tool {
  background: #f4f6fa;
  min-height: 100vh;
}

.condolence-editor {
  padding: 52px 0 88px;
}

.tool-container {
  max-width: 1760px;
  padding-left: 28px;
  padding-right: 28px;
}

.tool-heading {
  max-width: 860px;
  margin-bottom: 28px;
}

.tool-heading span {
  color: var(--primary);
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.tool-heading h1 {
  font-size: clamp(32px, 5vw, 54px);
  font-weight: 900;
  letter-spacing: 0;
  margin: 8px 0 10px;
}

.tool-heading p {
  color: #666565;
  font-size: 17px;
  margin: 0;
}

.tool-layout {
  display: grid;
  grid-template-columns: minmax(340px, 460px) minmax(720px, 1fr);
  gap: 28px;
  align-items: start;
}

.tool-form,
.preview-shell {
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(15, 23, 43, .08);
}

.tool-form {
  display: grid;
  gap: 18px;
  padding: 22px;
}

.tool-form label {
  color: var(--dark);
  font-weight: 800;
}

.tool-form .form-control {
  border-radius: 6px;
  min-height: 48px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.tool-actions {
  display: grid;
  gap: 10px;
}

.tool-actions .btn {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.preview-shell {
  padding: 18px;
  overflow: auto;
}

.preview-note {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #555;
  background: #f5f7fb;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  padding: 8px 12px;
  margin-bottom: 14px;
}

.preview-note i {
  color: var(--primary);
}

.poster-scale {
  width: min(100%, 720px);
  aspect-ratio: 4 / 5;
  margin: 0 auto;
}

.condolence-poster {
  position: relative;
  width: 1080px;
  height: 1350px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(145deg, rgba(15, 23, 43, .98), rgba(8, 13, 28, 1) 62%),
    var(--dark);
  transform: scale(var(--poster-scale));
  transform-origin: top left;
}

.poster-glow,
.poster-line {
  position: absolute;
  pointer-events: none;
}

.poster-glow-one {
  width: 760px;
  height: 760px;
  top: -360px;
  right: -260px;
  background: radial-gradient(circle, rgba(254, 161, 22, .24), rgba(254, 161, 22, 0) 68%);
}

.poster-glow-two {
  width: 680px;
  height: 680px;
  left: -320px;
  bottom: 120px;
  background: radial-gradient(circle, rgba(255, 255, 255, .08), rgba(255, 255, 255, 0) 70%);
}

.poster-line {
  left: 0;
  right: 0;
  height: 12px;
  background: linear-gradient(90deg, var(--primary), rgba(254, 161, 22, .2), var(--primary));
}

.poster-line-top {
  top: 0;
}

.poster-line-bottom {
  bottom: 0;
}

.poster-header,
.person-area,
.message-card,
.info-grid,
.poster-footer {
  position: relative;
  z-index: 2;
}

.poster-header {
  padding: 88px 82px 0;
  text-align: center;
}

.poster-header h2 {
  color: #fff;
  font-size: 76px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1;
  margin: 0;
}

.person-area {
  display: grid;
  justify-items: center;
  padding: 54px 84px 0;
  text-align: center;
}

.photo-frame {
  width: 282px;
  height: 282px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, .07);
  border: 6px solid rgba(254, 161, 22, .88);
  border-radius: 50%;
  box-shadow: 0 26px 70px rgba(0, 0, 0, .28);
  overflow: hidden;
}

.photo-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-frame.empty i {
  color: rgba(255, 255, 255, .42);
  font-size: 92px;
}

.person-area p {
  color: rgba(255, 255, 255, .58);
  font-size: 22px;
  font-weight: 800;
  margin: 28px 0 8px;
  text-transform: uppercase;
}

.person-area h3 {
  max-width: 860px;
  color: #fff;
  font-size: 58px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 0;
}

.message-card {
  width: auto;
  min-height: 230px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 46px 84px 0;
  background: rgba(255, 255, 255, .08);
  border: 1px solid rgba(255, 255, 255, .12);
  border-left: 10px solid var(--primary);
  padding: 38px 48px;
}

.message-text {
  color: rgba(255, 255, 255, .9);
  font-size: 34px;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
  white-space: pre-line;
  margin: 0;
}

.message-text.short {
  font-size: 38px;
  line-height: 1.36;
}

.message-text.medium {
  font-size: 32px;
}

.message-text.long {
  font-size: 27px;
  line-height: 1.38;
}

.message-text.very-long {
  font-size: 23px;
  line-height: 1.32;
}

.info-grid {
  position: absolute;
  left: 84px;
  right: 84px;
  bottom: 166px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.info-grid.count-1 {
  grid-template-columns: minmax(0, 1fr);
}

.info-grid.count-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-grid div {
  min-height: 112px;
  display: grid;
  grid-template-rows: 22px 1fr;
  gap: 10px;
  background: rgba(255, 255, 255, .08);
  border: 1px solid rgba(255, 255, 255, .12);
  border-bottom: 5px solid var(--primary);
  padding: 16px 18px 18px;
}

.info-grid small {
  color: rgba(255, 255, 255, .56);
  font-size: 15px;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
}

.info-grid span {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
}

.info-grid i {
  color: var(--primary);
  font-size: 25px;
}

.info-grid strong {
  color: #fff;
  font-size: 21px;
  font-weight: 900;
  line-height: 1.18;
}

.poster-footer {
  position: absolute;
  left: 84px;
  right: 84px;
  bottom: 48px;
  display: flex;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, .58);
  border-top: 1px solid rgba(255, 255, 255, .12);
  padding-top: 20px;
}

.poster-footer img {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  border-radius: 50%;
  object-fit: cover;
  opacity: .86;
}

.poster-footer div {
  display: grid;
  gap: 4px;
}

.poster-footer strong {
  color: rgba(255, 255, 255, .86);
  font-size: 20px;
  font-weight: 900;
}

.poster-footer span {
  font-size: 15px;
  line-height: 1.35;
}

@media (max-width: 991.98px) {
  .condolence-editor {
    padding: 36px 0 96px;
  }

  .tool-layout,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .tool-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .preview-shell {
    padding: 12px;
  }
}
</style>
